import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { canUseLapsedNursery, isLapsedPathAllowed } from "@/lib/lapsedAccess";

const ONBOARDING_DONE_KEY = "nursly.onboarding.done";


export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, loading, profile, role } = useAuth();
  const location = useLocation();
  const trial = useTrialAccess();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (trial.data?.trial_required && new URLSearchParams(location.search).get("upgrade") === "success") {
      supabase.functions.invoke("verify-trial-subscription").then(({ error }) => {
        if (!error) {
          void trial.refetch();
          void queryClient.invalidateQueries({ queryKey: ["nursery-plan"] });
        }
      });
    }
  }, [trial.data?.trial_required, location.search]);
  if (trial.data?.trial_required && trial.data.access === "full" && location.pathname === "/upgrade" && new URLSearchParams(location.search).get("upgrade") === "success") {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center mx-auto">
            <span className="text-primary-foreground font-display font-extrabold text-sm">C</span>
          </div>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (profile?.nursery_id && trial.isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  // A background failure retains the last verified plan. Keep the page mounted
  // so drafts survive; an initial failure still blocks entry without plan data.
  if (trial.isError && !trial.data) return <div className="p-8 text-destructive">We could not check your plan. Please refresh and try again.</div>;
  if (trial.data?.trial_required && trial.data.access === "setup" && !["/onboarding", "/upgrade", "/settings"].includes(location.pathname)) {
    return <Navigate to="/upgrade" replace />;
  }

  if (trial.data?.access === "lapsed") {
    if (!canUseLapsedNursery(role)) {
      return (
        <div className="min-h-screen bg-nursly-cream flex items-center justify-center p-6">
          <div className="max-w-md text-center space-y-4">
            <p className="font-display text-xl text-nursly-ink">Your nursery's Nursly subscription has ended. Please speak to your nursery owner.</p>
            <button className="min-h-11 px-4 underline text-sm" onClick={async () => { await supabase.auth.signOut(); window.location.href = "/login"; }}>Sign out</button>
          </div>
        </div>
      );
    }
    if (!isLapsedPathAllowed(location.pathname)) return <Navigate to="/children" replace />;
    return <>{children}</>;
  }

  // Setup is a one-time thing. Once an account has been seen as set up, this
  // browser never sends it back to the wizard, so toggling into the example
  // nursery and back can never resurface it.
  const onboardingDone = profile?.onboarding_completed === true;
  if (onboardingDone) {
    try { sessionStorage.setItem(ONBOARDING_DONE_KEY, "1"); } catch { /* private mode */ }
  }
  let seenDone = false;
  try { seenDone = sessionStorage.getItem(ONBOARDING_DONE_KEY) === "1"; } catch { /* private mode */ }

  // The choice screen sits before setup, so it must never be bounced back
  // into the wizard. Likewise, someone inside the example nursery is already
  // in a fully set-up nursery, so setup must never interrupt exploring.
  const preSetupPaths = ["/onboarding", "/explore-choice"];
  const exploring = !!profile?.explore_return_nursery_id;
  // Someone invited into an existing nursery never owns setup: they are joining
  // a nursery that is already running, so neither the choice screen nor the
  // wizard applies to them.
  const wasInvited = !!profile?.invited_by;
  if (profile && !onboardingDone && !seenDone && !exploring && !wasInvited && !preSetupPaths.includes(location.pathname)) {
    // Someone who signed up but has not yet picked how to start belongs on the
    // choice screen, whether they are arriving straight after verifying their
    // email or signing back in days later.
    const chosen = !!profile.onboarding_choice_made_at;
    return <Navigate to="/onboarding?redirected=true" replace />;
  }


  return <>{children}</>;
}
